import { Button, Flex, Icon, Text } from 'components/ui';
import { FC } from 'react';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { BillingCycleEnum, SubscriptionPlan } from '../../lib/api/generated';
import { getSubscriptionPriceAUD } from '../../lib/helpers';

export const SUBSCRIPTION_PRICE_CARD_BORDER_RADIUS = '32px';

const Container = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: space-between;
  padding: 28px;
  background: ${globalTheme.colors.white};
  border: 2px solid transparent;
  border-color: ${({ selected }) =>
    selected ? globalTheme.colors.pink : `transparent`};
  border-radius: ${SUBSCRIPTION_PRICE_CARD_BORDER_RADIUS};
  box-shadow: 0 32px 64px rgba(17, 17, 17, 0.08);
  transition: ${globalTheme.transitions.fast};
`;

const Name = styled(Text)`
  margin: 0 0 ${globalTheme.space[0]} 0;
  @media screen and (max-width: 768px) {
    font-size: ${globalTheme.space[3]};
  }
`;

const NoFeesBadge = styled.div`
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  color: ${globalTheme.colors.white};
  background-color: ${globalTheme.colors.pink};
  text-align: center;
  padding: 1px 20px;
  font-size: ${globalTheme.fontSizes[2]};
  border-radius: 0px 0px 12px 12px;
`;

const FeatureText = styled(Text)`
  font-size: ${globalTheme.fontSizes[3]};
  @media screen and (max-width: 768px) {
    font-size: ${globalTheme.fontSizes[5]};
  }
`;

const FeaturesContainer = styled.ul`
  display: grid;
  gap: 4px;
  margin: ${globalTheme.space[0]} 0 0;
`;

const FeatureContainer = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${globalTheme.space[1]};
  align-items: center;
  @media screen and (max-width: 768px) {
    ${Text} {
      font-size: ${globalTheme.fontSizes[3]};
    }
  }
`;

const FreeTrialInfoText = styled(Text)`
  @media screen and (max-width: 768px) {
    font-size: ${globalTheme.fontSizes[3]};
  }
`;

const SelectedText = styled(Text)`
  font-size: ${globalTheme.space[1]};
  @media screen and (max-width: 768px) {
    font-size: ${globalTheme.space[2]};
  }
`;

const Disc = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 100%;
  background-color: ${globalTheme.colors.black};
`;

const ButtonContainer = styled.div`
  display: grid;
  align-items: end;
  justify-content: center;
  margin-top: ${globalTheme.space[1]};
`;

const StyledButton = styled(Button)`
  min-width: 128px;
  @media screen and (max-width: 768px) {
    min-width: 158px;
    font-size: ${globalTheme.space[2]};
  }
`;

const SelectedContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: auto 1fr;
  gap: ${globalTheme.space[0]};
  align-items: center;
  height: 40px;
`;

const CheckIcon = styled(Icon)`
  background: ${globalTheme.colors.pink};
  border-radius: 50%;
`;

const MoreInfoText = styled(Text)`
  font-size: ${globalTheme.fontSizes[0]};
  color: ${globalTheme.colors['darkGrey']};
`;

export interface SubscriptionPriceCardProps {
  name: string;
  features?: {
    target: string;
    values: { feature: string; info?: string }[];
    bestFor?: string;
  };
  selectable?: boolean;
  selected?: boolean;
  onSelect?: VoidFunction;
  className?: string;
  additionalFeatures?: {
    plan: string;
    target: string;
    values: { feature: string; info?: string }[];
    bestFor?: string;
  }[];
  selectedTierPlans?: SubscriptionPlan[];
}

export const SubscriptionPriceCard: FC<SubscriptionPriceCardProps> = ({
  name,
  features,
  selectable = true,
  selected = false,
  onSelect,
  className,
  additionalFeatures,
  selectedTierPlans,
}) => {
  const priceGetter = (
    plans: SubscriptionPlan[] | undefined,
    billingCycle: BillingCycleEnum
  ): number | undefined => {
    if (!plans) {
      return undefined;
    }

    return plans
      ?.filter((plan: SubscriptionPlan) => plan.billing_cycle === billingCycle)
      .map((target: SubscriptionPlan) => target.price)[0];
  };

  return (
    <Container className={className} selected={selected}>
      <div>
        {name !== 'Free Plan' ? (
          <Flex gap={0} alignItems='center'>
            <Name
              fontSize={2}
              fontWeight={'semiBold'}
              color={'darkBlue'}
              lineHeight='heading'
              style={{
                textTransform: 'uppercase',
                marginTop: name === 'Agent' ? '5px' : '0px',
              }}
              maxWidth={'max-content'}
            >
              {name} Plan
            </Name>
            {name === 'Agent' && <NoFeesBadge>No transaction fees</NoFeesBadge>}
          </Flex>
        ) : (
          <Name
            fontSize={2}
            fontWeight={'semiBold'}
            color={'darkBlue'}
            style={{
              textTransform: 'uppercase',
            }}
          >
            {name}
          </Name>
        )}

        {name === 'Free Plan' && (
          <>
            <Name
              fontSize={4}
              lineHeight='heading'
              fontWeight={'semiBold'}
              color={'pink'}
            >
              $0{' '}
            </Name>
          </>
        )}
        {name !== 'Free Plan' && (
          <>
            <Name
              fontSize={4}
              lineHeight='heading'
              fontWeight={'semiBold'}
              color={'pink'}
            >
              {getSubscriptionPriceAUD(
                priceGetter(selectedTierPlans, BillingCycleEnum.Monthly)
              )}{' '}
              monthly /{' '}
              {getSubscriptionPriceAUD(
                priceGetter(selectedTierPlans, BillingCycleEnum.Annual)
              )}{' '}
              annual
            </Name>
          </>
        )}

        <FeaturesContainer>
          {name !== 'Free Plan' && (
            <FreeTrialInfoText
              lineHeight='heading'
              fontWeight='medium'
              fontStyle='italic'
              fontSize={1}
              color='blue'
            >
              {' '}
              *14 day free trial on monthly plan
            </FreeTrialInfoText>
          )}
          <FeatureText
            lineHeight='heading'
            color={'darkBlue'}
            fontWeight={'semiBold'}
          >
            {additionalFeatures && additionalFeatures[0].target}
          </FeatureText>
          {additionalFeatures &&
            additionalFeatures?.length > 0 &&
            additionalFeatures[0].values.map(
              (additional: { feature: string; info?: string }) => {
                return (
                  <FeatureContainer key={additional.feature}>
                    <Disc></Disc> <Text fontSize={2}>{additional.feature}</Text>
                  </FeatureContainer>
                );
              }
            )}
          {name === 'Free Plan' ? (
            <>
              <FeatureText
                lineHeight='heading'
                color={'darkBlue'}
                fontWeight={'semiBold'}
              >
                {features?.target}
              </FeatureText>
              {features?.values.map((feature) => {
                return (
                  <FeatureContainer key={feature.feature}>
                    <Disc></Disc>
                    <Text fontSize={2}>{feature.feature}</Text>
                  </FeatureContainer>
                );
              })}
            </>
          ) : (
            <></>
          )}
          <BestFor
            bestFor={
              features?.bestFor ||
              (additionalFeatures && additionalFeatures[0]?.bestFor)
            }
          />
        </FeaturesContainer>
      </div>
      <ButtonContainer>
        {selectable && !selected && (
          <StyledButton
            type='button'
            size='small'
            variant='primary'
            onClick={() => onSelect?.()}
            data-cy={`${name.replace(/\s+|[,\/]/g, '')}SelectButton`}
          >
            Select
          </StyledButton>
        )}
        {selectable && selected && (
          <SelectedContainer>
            <CheckIcon component={'CheckIcon'} color={'white'} size={2} />
            <SelectedText fontSize={1}>Selected</SelectedText>
          </SelectedContainer>
        )}
        {!selectable && (
          <Text color='pink' fontSize={3} fontWeight='bold'>
            Your current plan
          </Text>
        )}
      </ButtonContainer>
    </Container>
  );
};

const StyledText = styled(Text)`
  @media (max-width: 768px) {
    font-size: ${globalTheme.fontSizes[4]};
  }
`;

const BestFor = ({ bestFor }: { bestFor?: string }) => {
  if (!bestFor) {
    return null;
  }
  return (
    <Flex gap={0}>
      <StyledText
        color='blue'
        fontWeight={'semiBold'}
        fontSize={2}
        maxWidth={'max-content'}
        lineHeight='heading'
      >
        Best for:
      </StyledText>
      <StyledText fontStyle='italic' lineHeight='heading' fontSize={2}>
        {bestFor}
      </StyledText>
    </Flex>
  );
};
